import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { globalConfig } from './configuration/config';
import { authStore } from './core/stores/auth-store';
import { openModal } from './core/modal/open-modal';
import { InsufficientResourceDialog } from './components/dialogs/insufficient-resource-dialog';

type TokenGetter = () => Promise<string | undefined>;

export class Api {
  private axios: AxiosInstance;
  private getToken: TokenGetter | null = null;

  init() {
    this.axios = axios.create({
      baseURL: globalConfig.config.baseApiUrl,
    });

    this.axios.interceptors.request.use(async (config) => {
      if (this.getToken) {
        try {
          const token = await this.getToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch {
          // Token getter failed — leave Authorization off; the 401 response
          // path will kick the user back to sign in.
        }
      }

      return config;
    });

    this.axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error?.response?.status === 507) {
          openModal(InsufficientResourceDialog, { reason: 'storage-limit' });
        }

        if (error?.response?.status === 401) {
          authStore.setAuthorized(false);
        }

        return Promise.reject(error);
      },
    );
  }

  /**
   * Register the function that produces a fresh access token. AuthKit's
   * `getAccessToken()` handles cache + refresh internally, so we just call
   * it per request.
   */
  setTokenGetter(getter: TokenGetter) {
    this.getToken = getter;
  }

  /**
   * Returns the current access token (or undefined if the getter isn't
   * wired up yet). Used by app-socket for socket.io auth.
   */
  async getAccessToken(): Promise<string | undefined> {
    if (!this.getToken) return undefined;
    try {
      return await this.getToken();
    } catch {
      return undefined;
    }
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T | any> {
    const request = () => this.axios.get(url, { ...config });

    try {
      return await request();
    } catch (e) {
      return await this.handleError(e as AxiosError).then((res) => {
        if (res.retry) return request();
      });
    }
  }

  async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T> | any> {
    const request = () => this.axios.post(url, data, { ...config });

    try {
      return await request();
    } catch (e) {
      return await this.handleError(e as AxiosError).then((res) => {
        if (res.retry) return request();
      });
    }
  }

  updateSpaceInHeaders(spaceId: string) {
    this.axios.defaults.headers['X-Space-Id'] = spaceId;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T | any> {
    const request = () => this.axios.delete(url, { ...config });

    try {
      return await request();
    } catch (e) {
      return await this.handleError(e as AxiosError).then((res) => {
        if (res.retry) return request();
      });
    }
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T | any> {
    const request = () => this.axios.put(url, data, { ...config });

    try {
      return await request();
    } catch (e) {
      return await this.handleError(e as AxiosError).then((res) => {
        if (res.retry) return request();
      });
    }
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T | any> {
    const request = () => this.axios.patch(url, data, { ...config });
    return await request();
  }

  private async handleError(error: AxiosError): Promise<any> {
    if (!error.response) throw error;
    throw error;
  }
}

const api = new Api();

export { api };
